export const DOMUtils = {
  focusElement: (element: HTMLElement | null) => {
    element?.focus();
  },

  registerBadgeDeleteHandler: (
    element: HTMLElement | null,
    callback: () => void
  ) => {
    if (!element) return () => { };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('delete-badge')) {
        const badge = target.closest('.badge');
        if (badge) {
          badge.remove();
          callback();
        }
      }
    };

    element.addEventListener('click', handleClick);
    return () => element.removeEventListener('click', handleClick);
  },

  serializeContent: (node: Node): string => {
    let text = '';
    const childNodes = Array.from(node.childNodes);

    for (const child of childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent || '';
      } else if (child instanceof HTMLElement) {
        if (child.classList.contains('badge')) {
          const placeholder = child.dataset.placeholder;
          const url = child.dataset.url;
          text += placeholder ? `{{[${placeholder}]}}` : url || '';
        } else if ((child.tagName === 'SPAN' || child.tagName === 'DIV')) {
          text += '\n' + DOMUtils.serializeContent(child);
        } else {
          text += DOMUtils.serializeContent(child);
        }
      }
    }

    return text;
  },

  getUrlRegex: (domainOptions: string[]) => {
    return new RegExp(`(${domainOptions.join('|')})\\/\\w+`, 'g');
  },

  cleanEditableContent: (element: HTMLElement | null) => {
    if (!element) return;

    // Find all inner div elements
    const innerDivs = element.getElementsByTagName('span');
    Array.from(innerDivs).forEach((innerDiv) => {
      const isWrapper = innerDiv.classList.contains('badge-wrapper');

      if (!isWrapper) return;

      const children = Array.from(innerDiv.childNodes);
      let badgeIndex = -1;

      // Find the first badge in the div
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (
          child.nodeType === Node.ELEMENT_NODE &&
          (child as HTMLElement).classList.contains('badge')
        ) {
          badgeIndex = i;
          break;
        }
      }

      if (badgeIndex > 0) {
        // Remove all nodes before the badge
        for (let i = 0; i < badgeIndex; i++) {
          innerDiv.removeChild(children[i]);
        }

        // Check if the div now only contains the badge
        if (
          innerDiv.childNodes.length === 1 &&
          innerDiv.firstElementChild?.classList.contains('badge')
        ) {
          // Unwrap the badge
          const parent = innerDiv.parentNode;
          if (parent) {
            parent.insertBefore(innerDiv.firstElementChild, innerDiv);
            parent.removeChild(innerDiv);
          }
        }
      }
    });
  },

  createBadgeElement: (options: { placeholder?: string; url?: string, domainOptions?: string[]; }): HTMLElement => {
    const badge = document.createElement('span');
    badge.className = 'badge';
    badge.contentEditable = 'false';
    badge.style.cssText = `
      display: inline-flex;
      align-items: center;
      vertical-align: middle;
      border-radius: 6px;
      background-color: #E8E0FE;
      padding: 0 6px;
      margin: 2.5px 2px;
      height: 25px;
      white-space: nowrap;
      font-weight: 400;
      font-size: 12px;
      line-height: 16.8px;
      letter-spacing: 0%;
      color: #333333;
    `;

    if (options.placeholder) {
      badge.dataset.placeholder = options.placeholder;
      badge.appendChild(document.createTextNode(`{{[${options.placeholder}]}}`));
    } else if (options.url) {
      badge.dataset.url = options.url;
      badge.appendChild(document.createTextNode(options.url));
    }

    const deleteButton = document.createElement('button');
    deleteButton.className = 'delete-badge';
    deleteButton.textContent = '×';
    deleteButton.style.cssText = `
      margin-left: 4px;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
    `;

    badge.appendChild(deleteButton);
    return badge;
  },

  getCaretPosition: (editableDiv: HTMLElement): number => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return -1;

    const range = sel.getRangeAt(0);
    let count = 0;

    const traverseNodes = (node: Node): boolean => {
      if (node === range.startContainer) {
        if (node.nodeType === Node.TEXT_NODE) {
          count += range.startOffset;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Handle caret at start of badge-wrapper or badge span
          count += range.startOffset;
        }
        return true; // Found caret, stop traversal
      }

      if (node.nodeType === Node.TEXT_NODE) {
        count += node.textContent?.length || 0;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;

        if (el.classList.contains("badge")) {
          const placeholder = el.dataset.placeholder;
          const url = el.dataset.url;
          count += placeholder ? `{{[${placeholder}]}}`.length : (url?.length || 0);
          return false; // skip child nodes
        }

        // Treat badge-wrapper as inline container
        const isWrapper = el.classList.contains("badge-wrapper");
        if (isWrapper || el !== editableDiv) {
          count += 1; // Count wrappers as space/newline to simulate rendering
        }

        for (let i = 0; i < el.childNodes.length; i++) {
          const found = traverseNodes(el.childNodes[i]);
          if (found) return true;
        }
      }

      return false;
    };

    traverseNodes(editableDiv);
    return count;
  },

  handleEnterKey: (e: KeyboardEvent) => {
    if (e.key !== 'Enter') return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const { startContainer, startOffset } = range;

    const isTextNode = startContainer.nodeType === Node.TEXT_NODE;
    const isAtEndOfText =
      isTextNode && startOffset === (startContainer as Text).length;

    const isNextBadge = startContainer.nextSibling instanceof HTMLElement &&
      startContainer.nextSibling.classList.contains('badge');

    // Case 1: At end of text, next sibling is a badge
    if (isAtEndOfText && !isNextBadge) {
      const next = startContainer.nextSibling;

      if (
        next instanceof HTMLElement &&
        (next.classList.contains('badge') || next.classList.contains('badge-wrapper'))
      ) {
        e.preventDefault();
        const wrapper = document.createElement('span');
        wrapper.className = 'badge-wrapper';

        const space = document.createTextNode(' ');

        startContainer.parentNode?.insertBefore(wrapper, next);
        wrapper.appendChild(next);
        wrapper.appendChild(space);

        const newRange = document.createRange();
        newRange.setStart(space, 1);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
        return;
      }
    }

    // Case 2: Caret directly at start of badge-wrapper
    if (
      startContainer instanceof HTMLElement &&
      startContainer.classList.contains('badge-wrapper') &&
      startOffset === 0
    ) {
      e.preventDefault();
      const wrapper = document.createElement('span');
      wrapper.className = 'badge-wrapper';
      const space = document.createTextNode(' ');

      const parent = startContainer.parentNode;
      if (!parent) return;

      parent.insertBefore(wrapper, startContainer);
      wrapper.appendChild(startContainer);
      wrapper.appendChild(space);

      const newRange = document.createRange();
      newRange.setStart(space, 1);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
      return;
    }

    // Default fallback: let browser handle Enter
  },

  handleBackspace: (e: KeyboardEvent) => {
    if (e.key !== 'Backspace') return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const { startContainer, startOffset } = range;

    // Case: inside empty text node or at beginning of a line
    if (
      startOffset === 0 &&
      startContainer.nodeType === Node.TEXT_NODE &&
      startContainer.previousSibling instanceof HTMLElement &&
      startContainer.previousSibling.classList.contains('badge-wrapper')
    ) {
      e.preventDefault();

      const badge = startContainer.previousSibling;
      const parent = badge.parentNode;

      if (parent) {
        // Insert a space after the badge
        const spaceNode = document.createTextNode(' ');
        parent.insertBefore(spaceNode, startContainer);

        // Move caret to that space
        const newRange = document.createRange();
        newRange.setStart(spaceNode, 0);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    }
  }
};